import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = '/api';

/**
 * דאשבורד ציבורי לקבוצת שגרירים — the group's shared progress (sum of its
 * members) plus a per-member breakdown. Same capability-URL model as the
 * per-ambassador dashboard; linked from the admin groups panel, the manager
 * dashboard, and each member's personal dashboard.
 */
const PublicGroupDashboard: React.FC = () => {
  const { campaignSlug, groupSlug } = useParams<{ campaignSlug: string; groupSlug: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!campaignSlug || !groupSlug) return;
    axios.get(`${API_BASE}/public/group-dashboard/${campaignSlug}/${groupSlug}`)
      .then(res => setData(res.data))
      .catch(() => setError('הקבוצה לא נמצאה'))
      .finally(() => setLoading(false));
  }, [campaignSlug, groupSlug]);

  if (loading) return (
    <div dir="rtl" style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Heebo', sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '4px solid #e5e7eb', borderTop: '4px solid #7c3aed', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: '#6b7280' }}>טוען דאשבורד...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error || !data) return (
    <div dir="rtl" style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Heebo', sans-serif" }}>
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>😔</div>
        <p style={{ fontSize: 18, color: '#374151' }}>{error || 'שגיאה'}</p>
      </div>
    </div>
  );

  const { group, members, campaign, orgName, joinUrl, recentDonations } = data;
  const color = group.color || '#7c3aed';
  const fmt = (n: number) => Number(n || 0).toLocaleString('he-IL');
  const copy = (key: string, text: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 14, padding: 18, marginBottom: 16,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6',
  };

  return (
    <div dir="rtl" style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f5f3ff 0%, #f8fafc 40%)', fontFamily: "'Heebo', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');
      `}</style>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '24px 16px' }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
          borderRadius: 20, overflow: 'hidden', color: '#fff',
          boxShadow: '0 8px 32px rgba(124,58,237,0.3)', marginBottom: 20,
        }}>
          {group.imageUrl && (
            <img src={group.imageUrl} alt={group.name} style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }} />
          )}
          <div style={{ padding: '22px 24px', textAlign: 'center' }}>
            {!group.imageUrl && <div style={{ fontSize: 40, marginBottom: 8 }}>🎯</div>}
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>{group.name}</h1>
            {group.description && <p style={{ margin: '6px 0 0', fontSize: 13.5, opacity: 0.92 }}>{group.description}</p>}
            <p style={{ margin: '6px 0 0', fontSize: 14, opacity: 0.9 }}>קבוצת שגרירים בקמפיין "{campaign.name}"</p>
            <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.7 }}>{orgName}</p>
          </div>
        </div>

        {/* Group progress */}
        <div style={{ ...cardStyle, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, fontWeight: 700 }}>
            <span style={{ color }}>₪{fmt(group.raised)}</span>
            {group.goal > 0 && <span style={{ color: '#6b7280' }}>מתוך ₪{fmt(group.goal)}</span>}
          </div>
          <div style={{ width: '100%', height: 20, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{
              width: `${group.progress}%`, height: '100%',
              background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
              borderRadius: 999, transition: 'width 1s ease-out',
            }} />
          </div>
          {group.goal > 0 && (
            <p style={{ textAlign: 'center', marginTop: 8, fontSize: 20, fontWeight: 900, color }}>{group.progress}%</p>
          )}
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '16px 10px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#059669' }}>₪{fmt(group.raised)}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>סה"כ גויס</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '16px 10px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#2563eb' }}>{group.donorsCount}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>תורמים</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: '16px 10px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color }}>{group.membersCount}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>שגרירים</div>
          </div>
        </div>

        {/* Join-the-group link (only when the campaign allows self-join) */}
        {joinUrl && (
          <div style={cardStyle}>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 10 }}>➕ צירוף שגרירים לקבוצה</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input readOnly value={joinUrl} style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12, direction: 'ltr', color: '#6b7280' }} />
              <button
                onClick={() => copy('join', joinUrl)}
                style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: copied === 'join' ? '#22c55e' : '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >{copied === 'join' ? '✅ הועתק!' : '📋 העתק'}</button>
            </div>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`בואו להצטרף כשגרירים לקבוצת "${group.name}"! ${joinUrl}`)}`}
              target="_blank" rel="noreferrer"
              style={{ display: 'block', marginTop: 10, padding: '10px', borderRadius: 10, background: '#25D366', color: '#fff', textAlign: 'center', textDecoration: 'none', fontWeight: 700, fontSize: 13 }}
            >📱 הזמן שגרירים בוואטסאפ</a>
          </div>
        )}

        {/* Member ambassadors */}
        <div style={cardStyle}>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>🤝 השגרירים בקבוצה ({members.length})</p>
          {members.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🌱</div>
              <p style={{ fontSize: 14 }}>עדיין אין שגרירים בקבוצה</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {members.map((m: any, i: number) => (
                <a
                  key={m.slug}
                  href={`/campaign/${campaign.slug}/ambassador/${m.slug}/dashboard`}
                  target="_blank" rel="noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    background: '#f9fafb', borderRadius: 12, textDecoration: 'none',
                  }}
                  title="לדאשבורד האישי של השגריר"
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 12,
                    background: i === 0 ? '#f59e0b' : i === 1 ? '#9ca3af' : i === 2 ? '#b45309' : '#e5e7eb',
                    color: i < 3 ? '#fff' : '#374151',
                  }}>{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                      <span style={{ fontWeight: 800, fontSize: 14, color: '#059669', flexShrink: 0 }}>₪{fmt(m.raised)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                      <div style={{ flex: 1, height: 6, borderRadius: 999, background: '#e5e7eb', overflow: 'hidden' }}>
                        <div style={{ width: `${m.progress}%`, height: '100%', background: color, transition: 'width 0.5s' }} />
                      </div>
                      <span dir="ltr" style={{ fontSize: 10.5, fontWeight: 800, color, minWidth: 30, textAlign: 'end' }}>
                        {m.goal > 0 ? `${m.progress}%` : '—'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
                      {m.donorsCount} תורמים{m.goal > 0 ? ` · יעד ₪${fmt(m.goal)}` : ''}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Recent donations across the group */}
        <div style={cardStyle}>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 12 }}>💝 תרומות אחרונות בקבוצה</p>
          {recentDonations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0', color: '#9ca3af', fontSize: 13 }}>
              עדיין אין תרומות — שתפו את הלינקים האישיים כדי להתחיל!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentDonations.map((d: any, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f9fafb', borderRadius: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>{d.name}</span>
                    {d.ambassadorName && <span style={{ fontSize: 11, color: color, marginRight: 8 }}>דרך {d.ambassadorName}</span>}
                    <span style={{ fontSize: 12, color: '#9ca3af', marginRight: 8 }}>{new Date(d.date).toLocaleDateString('he-IL')}</span>
                  </div>
                  <span style={{ fontWeight: 800, fontSize: 15, color: '#059669', flexShrink: 0 }}>₪{fmt(d.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '24px 0 12px', color: '#9ca3af', fontSize: 11 }}>
          <p>מופעל ע"י <strong style={{ color: '#6b7280' }}>Amutech</strong> — משקיעים באנשים שעושים טוב</p>
        </div>
      </div>
    </div>
  );
};

export default PublicGroupDashboard;
